/* eslint-disable no-plusplus */
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import Checkbox from "@mui/material/Checkbox";
import { TableContainer } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { doc, updateDoc } from "firebase/firestore";
import { useAuth } from "layouts/authentication/AuthContext"; // Import useAuth
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker"; // Import DateTimePicker
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { db } from "../../../firebaseDb";

function BlockListDetail({ title, sprayInfo, shadow, setSprayInfo }) {
  const { user } = useAuth(); // Access the user object from AuthContext
  const [loadingStates, setLoadingStates] = useState({}); // Track loading states for each DateTimePicker
  const sprayInfoRef = useRef(sprayInfo);

  useEffect(() => {
    sprayInfoRef.current = sprayInfo;
  }, [sprayInfo]);

  function updateVarietyDateTime(
    blockVarieties,
    blockCode,
    varietyKey,
    timeType,
    newDateTime,
    clearedValue = null
  ) {
    return blockVarieties.map((blockVariety) => {
      if (blockVariety.block.code === blockCode) {
        const updatedVarieties = blockVariety.block.varieties.map((variety) => {
          if (variety.key === varietyKey) {
            return {
              ...variety,
              [timeType]: newDateTime ? newDateTime.toISOString() : clearedValue,
            };
          }
          return variety;
        });
        return { ...blockVariety, block: { ...blockVariety.block, varieties: updatedVarieties } };
      }
      return blockVariety;
    });
  }

  function handleCheckboxChange(blockCode, varietyKey, checked) {
    const updatedSprayInfo = { ...sprayInfo };
    updatedSprayInfo.blockVarieties = updatedSprayInfo.blockVarieties.map((blockVariety) => {
      if (blockVariety.block.code === blockCode) {
        const updatedVarieties = blockVariety.block.varieties.map((variety) => {
          if (variety.key === varietyKey) {
            return { ...variety, finalSpray: checked };
          }
          return variety;
        });
        return { ...blockVariety, block: { ...blockVariety.block, varieties: updatedVarieties } };
      }
      return blockVariety;
    });

    const updatedBlockVarieties = updatedSprayInfo.blockVarieties;
    const docRef = doc(db, "Sprays", sprayInfo.key);
    updateDoc(docRef, { blockVarieties: updatedBlockVarieties })
      .then(() => {
        console.log("Firestore updated successfully");
      })
      .catch((error) => {
        console.error("Error updating Firestore:", error);
      });
  }

  function handleDateTimeChange(blockCode, varietyKey, timeType, newDateTime, clearedValue = null) {
    const latestSprayInfo = sprayInfoRef.current;
    const updatedBlockVarieties = updateVarietyDateTime(
      latestSprayInfo.blockVarieties,
      blockCode,
      varietyKey,
      timeType,
      newDateTime,
      clearedValue
    );
    const nextSprayInfo = { ...latestSprayInfo, blockVarieties: updatedBlockVarieties };
    sprayInfoRef.current = nextSprayInfo;
    setSprayInfo(nextSprayInfo);
  }

  function handleDateTimeAccept(blockCode, varietyKey, timeType, newDateTime, clearedValue = null) {
    const latestSprayInfo = sprayInfoRef.current;
    const updatedBlockVarieties = updateVarietyDateTime(
      latestSprayInfo.blockVarieties,
      blockCode,
      varietyKey,
      timeType,
      newDateTime,
      clearedValue
    );
    const nextSprayInfo = { ...latestSprayInfo, blockVarieties: updatedBlockVarieties };
    sprayInfoRef.current = nextSprayInfo;
    setSprayInfo(nextSprayInfo);

    // Set loading state for the specific DateTimePicker
    setLoadingStates((prevState) => ({
      ...prevState,
      [`${blockCode}-${varietyKey}-${timeType}`]: true,
    }));

    const docRef = doc(db, "Sprays", latestSprayInfo.key);
    updateDoc(docRef, { blockVarieties: updatedBlockVarieties })
      .then(() => {
        console.log("Firestore updated successfully");
        // Reset loading state after saving
        setLoadingStates((prevState) => ({
          ...prevState,
          [`${blockCode}-${varietyKey}-${timeType}`]: false,
        }));
      })
      .catch((error) => {
        console.error("Error updating Firestore:", error);
        // Reset loading state on error
        setLoadingStates((prevState) => ({
          ...prevState,
          [`${blockCode}-${varietyKey}-${timeType}`]: false,
        }));
      });
  }

  function handleDateTimeClearOnEnter(blockCode, varietyKey, timeType, event) {
    if (event.key !== "Enter") return;
    if (event.target.value.trim() !== "") return;

    handleDateTimeAccept(blockCode, varietyKey, timeType, null, "");
  }

  function handleFillAllBlock(blockCode, sourceVariety) {
    if (!sourceVariety || !sourceVariety.startTime || !sourceVariety.endTime) return;

    setLoadingStates((prevState) => ({
      ...prevState,
      [`fill-all-${blockCode}`]: true,
    }));

    const updatedBlockVarieties = sprayInfo.blockVarieties.map((blockVariety) => {
      if (blockVariety.block.code !== blockCode) {
        return blockVariety;
      }

      return {
        ...blockVariety,
        block: {
          ...blockVariety.block,
          varieties: blockVariety.block.varieties.map((variety) =>
            variety.checked
              ? {
                  ...variety,
                  startTime: sourceVariety.startTime,
                  endTime: sourceVariety.endTime,
                }
              : variety
          ),
        },
      };
    });

    setSprayInfo((prevSprayInfo) => ({
      ...prevSprayInfo,
      blockVarieties: updatedBlockVarieties,
    }));

    const docRef = doc(db, "Sprays", sprayInfo.key);
    updateDoc(docRef, { blockVarieties: updatedBlockVarieties })
      .then(() => {
        setLoadingStates((prevState) => ({
          ...prevState,
          [`fill-all-${blockCode}`]: false,
        }));
      })
      .catch((error) => {
        console.error("Error updating Firestore:", error);
        setLoadingStates((prevState) => ({
          ...prevState,
          [`fill-all-${blockCode}`]: false,
        }));
      });
  }

  const filteredBlocks = sprayInfo.blockVarieties.filter((b) => b.block.checked);
  const hasFillAllColumn = filteredBlocks.some((block) => block.block.varieties.length > 1);
  for (let i = 0; i < filteredBlocks.length; i++) {
    filteredBlocks[i].block.varieties = filteredBlocks[i].block.varieties.filter((v) => v.checked);
  }

  return (
    <Card sx={{ height: "100%", boxShadow: !shadow && "none" }}>
      <MDBox pt={2} px={2}>
        <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
          {title}
        </MDTypography>
      </MDBox>
      <TableContainer>
        <Table aria-label="simple table">
          <TableRow>
            <TableCell align="left">Block</TableCell>
            <TableCell align="center">Variety</TableCell>
            <TableCell align="center">Hectares</TableCell>
            <TableCell align="center">Start Date & Time</TableCell>
            <TableCell align="center">End Date & Time</TableCell>
            {hasFillAllColumn && <TableCell align="center">Fill All</TableCell>}
            {user?.permissions === "Admin" && <TableCell align="center">Final Spray</TableCell>}
          </TableRow>

          <TableBody>
            {filteredBlocks.map((row, index) =>
              row.block.varieties.map((variety, varietyIndex) => (
                <TableRow
                  key={variety.key}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    backgroundColor: index % 2 === 0 ? "#b2d6bb" : "#e3fad4",
                  }}
                >
                  {varietyIndex === 0 && (
                    <TableCell align="left" rowSpan={row.block.varieties.length}>
                      {row.block.code}
                    </TableCell>
                  )}
                  <TableCell align="center">{variety.variety}</TableCell>
                  <TableCell align="center">{variety.hectares}</TableCell>
                  <TableCell align="center">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      {loadingStates[`${row.block.code}-${variety.key}-startTime`] ? (
                        <CircularProgress size={24} />
                      ) : (
                        <DateTimePicker
                          label="Start Date & Time"
                          value={variety.startTime ? dayjs(variety.startTime) : null}
                          onChange={(newDateTime) =>
                            handleDateTimeChange(
                              row.block.code,
                              variety.key,
                              "startTime",
                              newDateTime
                            )
                          }
                          onAccept={(newDateTime) =>
                            handleDateTimeAccept(
                              row.block.code,
                              variety.key,
                              "startTime",
                              newDateTime
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              onKeyDown={(event) =>
                                handleDateTimeClearOnEnter(
                                  row.block.code,
                                  variety.key,
                                  "startTime",
                                  event
                                )
                              }
                            />
                          )}
                        />
                      )}
                    </LocalizationProvider>
                  </TableCell>
                  <TableCell align="center">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      {loadingStates[`${row.block.code}-${variety.key}-endTime`] ? (
                        <CircularProgress size={24} />
                      ) : (
                        <DateTimePicker
                          label="End Date & Time"
                          value={variety.endTime ? dayjs(variety.endTime) : null}
                          onChange={(newDateTime) =>
                            handleDateTimeChange(
                              row.block.code,
                              variety.key,
                              "endTime",
                              newDateTime
                            )
                          }
                          onAccept={(newDateTime) =>
                            handleDateTimeAccept(
                              row.block.code,
                              variety.key,
                              "endTime",
                              newDateTime
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              onKeyDown={(event) =>
                                handleDateTimeClearOnEnter(
                                  row.block.code,
                                  variety.key,
                                  "endTime",
                                  event
                                )
                              }
                            />
                          )}
                        />
                      )}
                    </LocalizationProvider>
                  </TableCell>
                  {hasFillAllColumn && (
                    <TableCell align="center">
                      {varietyIndex === 0 && row.block.varieties.length > 1 && (
                        <MDButton
                          color="info"
                          size="small"
                          disabled={
                            !variety.startTime ||
                            !variety.endTime ||
                            loadingStates[`fill-all-${row.block.code}`] === true
                          }
                          onClick={() => handleFillAllBlock(row.block.code, variety)}
                        >
                          Fill All
                        </MDButton>
                      )}
                    </TableCell>
                  )}
                  {user?.permissions === "Admin" && (
                    <TableCell align="center">
                      <Checkbox
                        checked={variety.finalSpray}
                        onChange={(e) =>
                          handleCheckboxChange(row.block.code, variety.key, e.target.checked)
                        }
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

// Setting default props for the ProfilesList
BlockListDetail.defaultProps = {
  shadow: true,
};

// Typechecking props for the ProfilesList
BlockListDetail.propTypes = {
  title: PropTypes.string.isRequired,
  shadow: PropTypes.bool,
  sprayInfo: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default BlockListDetail;
